import { useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import apiClient from '@/lib/apiClient';
import { useAdminAssessments } from '@/hooks/useAdminAssessments';
import AdminStatsCards from '@/components/admin/AdminStatsCards';
import AssessmentTable from '@/components/admin/AssessmentTable';
import AdminHeader from '@/components/admin/AdminHeader';
import FilterControls from '@/components/admin/FilterControls';

// Module-level helpers — stable identities.
const triggerBlobDownload = (blob, filename) => {
  const url = window.URL.createObjectURL(new Blob([blob]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const exportCsv = () =>
  apiClient.get('/admin/export-csv', { responseType: 'blob' })
    .then(r => { triggerBlobDownload(r.data, 'assessments_export.csv'); toast.success('CSV exported successfully'); })
    .catch(() => toast.error('Failed to export CSV'));

const LoadingScreen = () => (
  <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
    <div className="text-center">
      <div className="w-16 h-16 border-4 border-teal-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
      <p className="text-slate-600">Loading...</p>
    </div>
  </div>
);

const AdminDashboardContainer = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading, logout } = useAuth();
  const goToLogin = useCallback(() => navigate('/admin/login'), [navigate]);
  const admin = useAdminAssessments({ user, authLoading, onUnauthorized: goToLogin });

  useEffect(() => {
    if (!authLoading && !user) goToLogin();
  }, [user, authLoading, goToLogin]);

  if (authLoading || admin.loading) return <LoadingScreen />;

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <AdminHeader userName={user?.name} onLogout={() => logout().then(goToLogin)} />
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-12">
        <AdminStatsCards assessments={admin.assessments} />
        <FilterControls
          search={admin.search} setSearch={admin.setSearch}
          filterStability={admin.filterStability} setFilterStability={admin.setFilterStability}
          filterStatus={admin.filterStatus} setFilterStatus={admin.setFilterStatus}
          onSearch={admin.refresh} onExport={exportCsv}
        />
        <AssessmentTable assessments={admin.assessments} onStatusChange={admin.updateStatus} onChanged={admin.refresh} />
      </div>
    </div>
  );
};

export default AdminDashboardContainer;
